import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AttachMoney as CommissionIcon,
  TrendingUp as PerformanceIcon,
  Percent as PercentIcon,
  AccountBalance as PaymentIcon,
  Settings as SettingsIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Star as PremiumIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  LocalOffer as DiscountIcon,
  EmojiEvents as BonusIcon,
  Info as InfoIcon,
  Timeline as TierIcon,
} from "@mui/icons-material";
import AlertModal from "../../../components/AlertModal";
import ConfirmModal from "../../../components/ConfirmModal";


const SalesCommissionSetup = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Sample commission plans
  const [commissionPlans, setCommissionPlans] = useState([
    {
      id: 1,
      planCode: "PLAN001",
      planName: "Distributor Premium",
      partnerType: "Distributor",
      baseRate: 15,
      minOrderValue: 50000,
      paymentCycle: "Monthly",
      status: "Active",
      createdDate: "2023-05-01",
      partnersCount: 3,
      totalCommissionPaid: 525000,
      description: "Premium plan for high-volume distributors",
      tiers: [
        { from: 0, to: 500000, rate: 15 },
        { from: 500001, to: 1000000, rate: 17 },
        { from: 1000001, to: null, rate: 20 },
      ],
      categoryRates: [
        { category: "Electronics", rate: 12 },
        { category: "Apparel", rate: 18 },
        { category: "Home & Kitchen", rate: 15 },
      ],
      performanceBonus: {
        enabled: true,
        threshold: 1000000,
        bonusRate: 3,
      },
    },
    {
      id: 2,
      planCode: "PLAN002",
      planName: "Reseller Standard",
      partnerType: "Reseller",
      baseRate: 10,
      minOrderValue: 20000,
      paymentCycle: "Monthly",
      status: "Active",
      createdDate: "2023-06-10",
      partnersCount: 4,
      totalCommissionPaid: 168000,
      description: "Standard commission plan for resellers",
      tiers: [
        { from: 0, to: 300000, rate: 10 },
        { from: 300001, to: 600000, rate: 12 },
        { from: 600001, to: null, rate: 15 },
      ],
      categoryRates: [
        { category: "Electronics", rate: 8 },
        { category: "Apparel", rate: 12 },
        { category: "Home & Kitchen", rate: 10 },
      ],
      performanceBonus: {
        enabled: true,
        threshold: 500000,
        bonusRate: 2,
      },
    },
    {
      id: 3,
      planCode: "PLAN003",
      planName: "Affiliate Basic",
      partnerType: "Affiliate",
      baseRate: 5,
      minOrderValue: 5000,
      paymentCycle: "Monthly",
      status: "Active",
      createdDate: "2023-07-20",
      partnersCount: 2,
      totalCommissionPaid: 35000,
      description: "Basic commission plan for affiliate partners",
      tiers: [
        { from: 0, to: 100000, rate: 5 },
        { from: 100001, to: 300000, rate: 6 },
        { from: 300001, to: null, rate: 7 },
      ],
      categoryRates: [
        { category: "Electronics", rate: 4 },
        { category: "Apparel", rate: 6 },
        { category: "Home & Kitchen", rate: 5 },
      ],
      performanceBonus: {
        enabled: false,
        threshold: 0,
        bonusRate: 0,
      },
    },
    {
      id: 4,
      planCode: "PLAN004",
      planName: "Agent Commission",
      partnerType: "Agent",
      baseRate: 7,
      minOrderValue: 10000,
      paymentCycle: "Monthly",
      status: "Active",
      createdDate: "2023-08-15",
      partnersCount: 1,
      totalCommissionPaid: 15600,
      description: "Commission plan for individual sales agents",
      tiers: [
        { from: 0, to: 200000, rate: 7 },
        { from: 200001, to: 500000, rate: 8 },
        { from: 500001, to: null, rate: 10 },
      ],
      categoryRates: [
        { category: "Electronics", rate: 6 },
        { category: "Apparel", rate: 8 },
        { category: "Home & Kitchen", rate: 7 },
      ],
      performanceBonus: {
        enabled: true,
        threshold: 300000,
        bonusRate: 1.5,
      },
    },
    {
      id: 5,
      planCode: "PLAN005",
      planName: "Special Promotional",
      partnerType: "Reseller",
      baseRate: 12,
      minOrderValue: 15000,
      paymentCycle: "Bi-Weekly",
      status: "Inactive",
      createdDate: "2023-09-01",
      partnersCount: 0,
      totalCommissionPaid: 0,
      description: "Limited time promotional commission plan",
      tiers: [
        { from: 0, to: 250000, rate: 12 },
        { from: 250001, to: 500000, rate: 14 },
        { from: 500001, to: null, rate: 16 },
      ],
      categoryRates: [
        { category: "Electronics", rate: 10 },
        { category: "Apparel", rate: 14 },
        { category: "Home & Kitchen", rate: 12 },
      ],
      performanceBonus: {
        enabled: false,
        threshold: 0,
        bonusRate: 0,
      },
    },
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);

  // Modal states
  const [alertConfig, setAlertConfig] = useState({ open: false, title: 'Alert', message: '', type: 'info' });
  const [confirmConfig, setConfirmConfig] = useState({ open: false, title: 'Confirm', message: '', onConfirm: null });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };

  const showConfirm = (message, onConfirm, title = 'Confirm') => {
    setConfirmConfig({ open: true, title, message, onConfirm });
  };


  // Form state
  const [planForm, setPlanForm] = useState({
    planCode: "",
    planName: "",
    partnerType: "Reseller",
    baseRate: 10,
    minOrderValue: 10000,
    paymentCycle: "Monthly",
    description: "",
    status: "Active",
    performanceBonusEnabled: false,
    performanceThreshold: 0,
    bonusRate: 0,
  });

  // Helper functions
  const getTypeColor = (type) => {
    switch (type) {
      case 'Distributor': return 'primary';
      case 'Reseller': return 'secondary';
      case 'Affiliate': return 'info';
      case 'Agent': return 'warning';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handlers
  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      planCode: plan.planCode,
      planName: plan.planName,
      partnerType: plan.partnerType,
      baseRate: plan.baseRate,
      minOrderValue: plan.minOrderValue,
      paymentCycle: plan.paymentCycle,
      description: plan.description,
      status: plan.status,
      performanceBonusEnabled: plan.performanceBonus.enabled,
      performanceThreshold: plan.performanceBonus.threshold,
      bonusRate: plan.performanceBonus.bonusRate,
    });
    setOpenModal(true);
  };

  const handleView = (plan) => {
    setViewingPlan(plan);
    setOpenViewModal(true);
  };

  const handleDelete = (id) => {
    showConfirm("Are you sure you want to delete this commission plan?", () => {
      setCommissionPlans(commissionPlans.filter(plan => plan.id !== id));
      setSuccessMessage("Commission plan deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    });
  };


  const handleToggleStatus = (plan) => {
    setCommissionPlans(commissionPlans.map(p =>
      p.id === plan.id
        ? { ...p, status: p.status === 'Active' ? 'Inactive' : 'Active' }
        : p
    ));
    setSuccessMessage(`${plan.planName} ${plan.status === 'Active' ? 'deactivated' : 'activated'} successfully!`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = () => {
    setEditingPlan(null);
    const newCode = `PLAN${String(commissionPlans.length + 1).padStart(3, '0')}`;
    setPlanForm({
      planCode: newCode,
      planName: "",
      partnerType: "Reseller",
      baseRate: 10,
      minOrderValue: 10000,
      paymentCycle: "Monthly",
      description: "",
      status: "Active",
      performanceBonusEnabled: false,
      performanceThreshold: 0,
      bonusRate: 0,
    });
    setOpenModal(true);
  };

  const handleSavePlan = () => {
    if (!planForm.planName) {
      showAlert("Plan name is required", "warning", "Validation Error");
      return;
    }

    const newPlan = {
      id: editingPlan ? editingPlan.id : commissionPlans.length + 1,
      planCode: planForm.planCode,
      planName: planForm.planName,
      partnerType: planForm.partnerType,
      baseRate: parseFloat(planForm.baseRate),
      minOrderValue: parseFloat(planForm.minOrderValue),
      paymentCycle: planForm.paymentCycle,
      status: planForm.status,
      createdDate: editingPlan ? editingPlan.createdDate : new Date().toISOString().split('T')[0],
      partnersCount: editingPlan ? editingPlan.partnersCount : 0,
      totalCommissionPaid: editingPlan ? editingPlan.totalCommissionPaid : 0,
      description: planForm.description,
      tiers: editingPlan ? editingPlan.tiers : [
        { from: 0, to: 300000, rate: planForm.baseRate },
        { from: 300001, to: 600000, rate: planForm.baseRate + 2 },
        { from: 600001, to: null, rate: planForm.baseRate + 5 },
      ],
      categoryRates: editingPlan ? editingPlan.categoryRates : [
        { category: "Electronics", rate: planForm.baseRate - 2 },
        { category: "Apparel", rate: planForm.baseRate + 2 },
        { category: "Home & Kitchen", rate: planForm.baseRate },
      ],
      performanceBonus: {
        enabled: planForm.performanceBonusEnabled,
        threshold: parseFloat(planForm.performanceThreshold) || 0,
        bonusRate: parseFloat(planForm.bonusRate) || 0,
      },
    };

    if (editingPlan) {
      setCommissionPlans(commissionPlans.map(p => p.id === editingPlan.id ? newPlan : p));
      setSuccessMessage("Commission plan updated successfully!");
    } else {
      setCommissionPlans([newPlan, ...commissionPlans]);
      setSuccessMessage("Commission plan created successfully!");
    }

    setOpenModal(false);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getFilteredPlans = () => {
    if (activeTab === 0) return commissionPlans;
    if (activeTab === 1) return commissionPlans.filter(p => p.status === "Active");
    if (activeTab === 2) return commissionPlans.filter(p => p.status === "Inactive");
    return commissionPlans;
  };

  const filteredPlans = getFilteredPlans();

  // Calculate statistics
  const totalPlans = commissionPlans.length;
  const activePlans = commissionPlans.filter(p => p.status === "Active").length;
  const totalPartners = commissionPlans.reduce((sum, p) => sum + p.partnersCount, 0);
  const totalCommission = commissionPlans.reduce((sum, p) => sum + p.totalCommissionPaid, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Commission Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure commission plans and rates for sales partners • {filteredPlans.length} plans
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Create Plan
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Plans</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalPlans}
                  </Typography>
                </Box>
                <SettingsIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Active Plans</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {activePlans}
                  </Typography>
                </Box>
                <ActiveIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Partners</Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {totalPartners}
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Commission</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {formatCurrency(totalCommission)}
                  </Typography>
                </Box>
                <CommissionIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Plans" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Active" icon={<ActiveIcon />} iconPosition="start" />
          <Tab label="Inactive" icon={<InactiveIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Plan Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Partner Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Commission Rate</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Min Order</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Payment Cycle</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Partners</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Paid</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlans.length > 0 ? (
                filteredPlans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((plan) => (
                  <TableRow key={plan.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                          <CommissionIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="700">{plan.planName}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {plan.planCode}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.partnerType}
                        size="small"
                        color={getTypeColor(plan.partnerType)}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PercentIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="h6" fontWeight="bold" color="warning.main">
                          {plan.baseRate}%
                        </Typography>
                      </Box>
                      {plan.performanceBonus.enabled && (
                        <Chip
                          icon={<BonusIcon />}
                          label={`+${plan.performanceBonus.bonusRate}% Bonus`}
                          size="small"
                          sx={{ mt: 0.5, bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {formatCurrency(plan.minOrderValue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={plan.paymentCycle} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6" fontWeight="bold" color="secondary.main">
                        {plan.partnersCount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600" color="success.main">
                        {formatCurrency(plan.totalCommissionPaid)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={plan.status}
                          size="small"
                          sx={{
                            backgroundColor: plan.status === 'Active' ? '#e8f5e9' : '#ffebee',
                            color: plan.status === 'Active' ? '#2e7d32' : '#d32f2f',
                            fontWeight: 600,
                          }}
                        />
                        <Switch
                          checked={plan.status === 'Active'}
                          onChange={() => handleToggleStatus(plan)}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(plan)} sx={{ color: 'info.main' }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(plan)} sx={{ color: 'warning.main' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(plan.id)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <SettingsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">No commission plans found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPlans.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Create/Edit Plan Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon /> {editingPlan ? 'Edit' : 'Create'} Commission Plan
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Plan Code" value={planForm.planCode} disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Partner Type</InputLabel>
                <Select
                  value={planForm.partnerType}
                  onChange={(e) => setPlanForm({ ...planForm, partnerType: e.target.value })}
                  label="Partner Type"
                >
                  <MenuItem value="Distributor">Distributor</MenuItem>
                  <MenuItem value="Reseller">Reseller</MenuItem>
                  <MenuItem value="Affiliate">Affiliate</MenuItem>
                  <MenuItem value="Agent">Agent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name *"
                value={planForm.planName}
                onChange={(e) => setPlanForm({ ...planForm, planName: e.target.value })}
                placeholder="e.g., Premium Distributor Plan"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                placeholder="Brief description of the commission plan"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Base Commission Rate (%)"
                value={planForm.baseRate}
                onChange={(e) => setPlanForm({ ...planForm, baseRate: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Order Value"
                value={planForm.minOrderValue}
                onChange={(e) => setPlanForm({ ...planForm, minOrderValue: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Payment Cycle</InputLabel>
                <Select
                  value={planForm.paymentCycle}
                  onChange={(e) => setPlanForm({ ...planForm, paymentCycle: e.target.value })}
                  label="Payment Cycle"
                >
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Performance Bonus (Optional)
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={planForm.performanceBonusEnabled}
                    onChange={(e) => setPlanForm({ ...planForm, performanceBonusEnabled: e.target.checked })}
                  />
                }
                label="Enable Performance Bonus"
              />
            </Grid>
            {planForm.performanceBonusEnabled && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Performance Threshold"
                    value={planForm.performanceThreshold}
                    onChange={(e) => setPlanForm({ ...planForm, performanceThreshold: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                    }}
                    helperText="Minimum sales to qualify for bonus"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Bonus Rate"
                    value={planForm.bonusRate}
                    onChange={(e) => setPlanForm({ ...planForm, bonusRate: e.target.value })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    helperText="Additional commission percentage"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Tiered rates and category-specific rates can be configured after creating the plan.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePlan}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            {editingPlan ? 'Update Plan' : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />

      <ConfirmModal
        open={confirmConfig.open}
        title={confirmConfig.title}
        onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
        onConfirm={() => {
          if (confirmConfig.onConfirm) confirmConfig.onConfirm();
          setConfirmConfig({ ...confirmConfig, open: false });
        }}
        type="warning"
      >
        {confirmConfig.message}
      </ConfirmModal>

      {/* View Plan Details Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon /> Commission Plan Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewingPlan && (
            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Plan Code</Typography>
                      <Typography variant="body1" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                        {viewingPlan.planCode}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Partner Type</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip label={viewingPlan.partnerType} color={getTypeColor(viewingPlan.partnerType)} />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{viewingPlan.description}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Commission Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Commission Structure
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', textAlign: 'center' }}>
                  <PercentIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">Base Rate</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {viewingPlan.baseRate}%
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(83, 109, 254, 0.05)', textAlign: 'center' }}>
                  <PaymentIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">Min Order Value</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {formatCurrency(viewingPlan.minOrderValue)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', textAlign: 'center' }}>
                  <CommissionIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="h6" fontWeight="bold" color="secondary.main">
                    {formatCurrency(viewingPlan.totalCommissionPaid)}
                  </Typography>
                </Paper>
              </Grid>

              {/* Tiered Rates */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <TierIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Tiered Commission Rates
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {viewingPlan.tiers.map((tier, index) => (
                    <ListItem key={index} sx={{ bgcolor: '#f5f5f5', mb: 1, borderRadius: 1 }}>
                      <ListItemIcon>
                        <TierIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {formatCurrency(tier.from)} - {tier.to ? formatCurrency(tier.to) : 'Above'}
                          </Typography>
                        }
                        secondary={`Commission: ${tier.rate}%`}
                      />
                      <Chip label={`${tier.rate}%`} color="warning" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              {/* Category Rates */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <CategoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Category-Specific Rates
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {viewingPlan.categoryRates.map((cat, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                        <Typography variant="caption" color="text.secondary">{cat.category}</Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          {cat.rate}%
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Performance Bonus */}
              {viewingPlan.performanceBonus.enabled && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 193, 7, 0.1)', borderLeft: '4px solid #ffc107' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BonusIcon sx={{ color: '#e65100' }} />
                      <Typography variant="subtitle1" fontWeight="bold">Performance Bonus</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Threshold</Typography>
                        <Typography variant="body1" fontWeight="600">
                          {formatCurrency(viewingPlan.performanceBonus.threshold)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Bonus Rate</Typography>
                        <Typography variant="h6" fontWeight="bold" color="warning.main">
                          +{viewingPlan.performanceBonus.bonusRate}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setOpenViewModal(false);
              handleEdit(viewingPlan);
            }}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Edit Plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesCommissionSetup;

